import { useMemo, type ReactNode } from 'react'
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'

import {
  WorkflowExecutionStatus,
  WorkflowNodeType,
  type WorkflowDefinition,
} from '@/gen/cortado/v1/cortado_pb'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatTimestamp } from '@/lib/date-utils'
import {
  useGetWorkflowExecution,
  useRetryWorkflowExecution,
  useWorkflowDefinitions,
} from '@/stores/workflows.store'
import { ConditionTraceTable } from './condition-trace-table'
import { nodeFromProto } from './graph/graph-types'
import { nodeDisplay } from './graph/node-display'
import {
  stoppedByEventTypeLabel,
  workflowExecutionStatusLabel,
  workflowExecutionStatusVariant,
  workflowExecutionStepStatusLabel,
  workflowExecutionStepStatusVariant,
} from './workflow-execution-status'

interface WorkflowExecutionDetailSheetProps {
  executionId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Maps the raw action_type string stored on execution steps to a human label. */
function humanizeActionType(raw: string | undefined): string {
  if (!raw) return '-'
  switch (raw.toUpperCase()) {
    case 'SEND_TEMPLATED_EMAIL':
      return 'Send templated email'
    case 'SEND_EMAIL':
      return 'Send email'
    case 'SEND_NOTIFICATION':
      return 'Send notification'
    case 'WAIT':
      return 'Wait'
    case 'CREATE_TASK':
      return 'Create task'
    case 'UPDATE_FIELD':
      return 'Update field'
    case 'SEND_WEBHOOK':
      return 'Send webhook'
    case 'BRANCH':
      return 'Branch check'
    case 'SWITCH':
      return 'Switch route'
    case 'STOP':
      return 'Stopped'
    default:
      // Humanize unknown values: "SOME_ACTION" → "Some action"
      return raw
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase())
  }
}

/**
 * parseStepResultData reads the untyped result_data JSON of an execution step.
 * Two things still come from here:
 *
 *   - `edge`: the branch decision ({"matched": true, "edge": "true"}). There is
 *     no typed equivalent on WorkflowExecutionStep yet, so this is the only
 *     source.
 *   - `node_id`: the LEGACY fallback for node identity. Steps recorded before
 *     migration 000329 have no `node_id` column, so the id only exists in this
 *     blob. New rows carry `WorkflowExecutionStep.node_id` and never need it.
 *
 * Returns empty fields when the JSON is absent or malformed.
 */
function parseStepResultData(resultData: string | undefined): {
  edge?: string
  caseLabel?: string
  legacyNodeId?: string
  statusCode?: number
} {
  if (!resultData) return {}
  try {
    const data = JSON.parse(resultData) as Record<string, unknown>
    return {
      edge: typeof data.edge === 'string' ? data.edge : undefined,
      caseLabel: typeof data.case_label === 'string' ? data.case_label : undefined,
      legacyNodeId: typeof data.node_id === 'string' ? data.node_id : undefined,
      // SEND_WEBHOOK records the endpoint's HTTP status so the run view can
      // show what the receiver answered.
      statusCode: typeof data.status_code === 'number' ? data.status_code : undefined,
    }
  } catch {
    // Malformed JSON — silently ignore
    return {}
  }
}

/** "took True path" / "took False path" for a BRANCH step's recorded edge. */
function branchAnnotation(edge: string | undefined): string | undefined {
  if (!edge) return undefined
  const label = edge === 'true' ? 'True' : edge === 'false' ? 'False' : edge
  return `took ${label} path`
}

/** "responded 204" for a SEND_WEBHOOK step that recorded the endpoint's status. */
export function webhookAnnotation(
  actionType: string | undefined,
  statusCode: number | undefined
): string | undefined {
  if (actionType?.toUpperCase() !== 'SEND_WEBHOOK' || statusCode === undefined) return undefined
  return `responded ${statusCode}`
}

function routingAnnotation(
  actionType: string | undefined,
  edge: string | undefined,
  caseLabel: string | undefined
): string | undefined {
  if (actionType?.toUpperCase() === 'SWITCH') {
    return edge
      ? `took ${caseLabel || (edge === 'otherwise' ? 'Otherwise' : edge)} path`
      : undefined
  }
  return branchAnnotation(edge)
}

/** A current-graph node's display label plus the type it carries today. */
export type NodeLabelInfo = { label: string; nodeType: WorkflowNodeType }

/**
 * nodeLabelsById maps each node of a definition's CURRENT graph to a display
 * label + node type, for showing "Email to Contact email" on a run's step
 * instead of the bare action type.
 *
 * The lookup is deliberately best-effort: a run executes a frozen
 * `graph_snapshot`, so a node DELETED since the run simply has no entry here
 * and the step falls back to its humanized action type. A node kept its id
 * but was EDITED IN PLACE (retyped, repointed) still resolves here — that's
 * why the node's current type is carried alongside the label: stepDisplayLabel
 * only trusts the label when that type still matches what the step actually
 * recorded running. Exported for tests.
 */
export function nodeLabelsById(
  definition: WorkflowDefinition | undefined
): Map<string, NodeLabelInfo> {
  const labels = new Map<string, NodeLabelInfo>()
  for (const node of definition?.nodes ?? []) {
    if (!node.id) continue
    const label = node.label.trim() || nodeDisplay(nodeFromProto(node), definition?.trigger).title
    if (label) labels.set(node.id, { label, nodeType: node.nodeType })
  }
  return labels
}

/**
 * stepDisplayLabel decides whether a step may borrow its node's current-graph
 * label as its title. An id match alone is not enough: a node edited in place
 * (retyped, repointed) keeps its id, so its CURRENT label can describe an
 * action the step never performed. Only trust the label when the current
 * node's type still matches the step's own recorded `action_type` — the
 * step's authoritative fact. Otherwise fall back to the humanized action type
 * (the caller does this via `nodeLabel ?? actionLabel`). Exported for tests.
 */
export function stepDisplayLabel(
  nodeInfo: NodeLabelInfo | undefined,
  actionType: string | undefined
): string | undefined {
  if (!nodeInfo) return undefined
  if (WorkflowNodeType[nodeInfo.nodeType] !== actionType?.toUpperCase()) return undefined
  return nodeInfo.label
}

export function WorkflowExecutionDetailSheet({
  executionId,
  open,
  onOpenChange,
}: WorkflowExecutionDetailSheetProps) {
  const { execution, steps, conditionTrace, isLoading, isError } = useGetWorkflowExecution(
    executionId,
    { enabled: open }
  )
  const { retryExecution, isPending } = useRetryWorkflowExecution()

  // The run's definition, for turning a step's node_id into a human label.
  // Sourced from the org LIST rather than a per-id get: every surface that
  // mounts this sheet already loads that list to label the run rows, and a run
  // whose definition has since been deleted simply finds nothing instead of
  // taking a 404 path.
  const { workflowDefinitions } = useWorkflowDefinitions({ enabled: open })
  const nodeLabels = useMemo(() => {
    const definition = workflowDefinitions.find(
      (candidate) => candidate.id === execution?.workflowDefinitionId
    )
    return nodeLabelsById(definition)
  }, [workflowDefinitions, execution?.workflowDefinitionId])

  const canRetry = execution?.status === WorkflowExecutionStatus.FAILED
  const stoppedBy =
    execution?.status === WorkflowExecutionStatus.STOPPED
      ? stoppedByEventTypeLabel(execution.stoppedByEventType)
      : undefined

  const handleRetry = async () => {
    if (!execution) return

    try {
      await retryExecution(execution.id)
      toast.success('Workflow execution retry started')
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to retry workflow execution', {
        description: error instanceof Error ? error.message : 'Please try again.',
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[46rem]">
        <SheetHeader>
          <SheetTitle>Execution detail</SheetTitle>
          <SheetDescription>
            {execution
              ? formatTimestamp(execution.createdAt, 'MMM d, yyyy h:mm a')
              : 'Loading run data'}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-4">
          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Execution could not be loaded</AlertTitle>
              <AlertDescription>Refresh the page and try again.</AlertDescription>
            </Alert>
          )}

          {execution && (
            <>
              <div className="grid gap-3 rounded-md border bg-muted/20 p-4 sm:grid-cols-2">
                <DetailItem label="Status">
                  <div className="flex items-center gap-2">
                    <Badge variant={workflowExecutionStatusVariant(execution.status)}>
                      {workflowExecutionStatusLabel(execution.status)}
                    </Badge>
                    {execution.manuallyTriggered && (
                      <Badge variant="outline" className="text-muted-foreground">
                        Manual
                      </Badge>
                    )}
                  </div>
                </DetailItem>
                <DetailItem label="Retry count">{execution.retryCount}</DetailItem>
                <DetailItem label="Matched">
                  {formatTimestamp(execution.matchedAt, 'MMM d, yyyy h:mm a')}
                </DetailItem>
                <DetailItem label="Started">
                  {execution.startedAt
                    ? formatTimestamp(execution.startedAt, 'MMM d, yyyy h:mm a')
                    : '-'}
                </DetailItem>
                <DetailItem label="Completed">
                  {execution.completedAt
                    ? formatTimestamp(execution.completedAt, 'MMM d, yyyy h:mm a')
                    : '-'}
                </DetailItem>
                <DetailItem label="Source resource">{execution.sourceResourceId || '-'}</DetailItem>
                {stoppedBy && <DetailItem label="Stopped by">{stoppedBy}</DetailItem>}
              </div>

              {execution.errorMessage && (
                <Alert
                  variant={
                    execution.status === WorkflowExecutionStatus.FAILED ? 'destructive' : 'default'
                  }
                >
                  <AlertTriangle />
                  <AlertTitle>
                    {execution.status === WorkflowExecutionStatus.CONDITION_UNMET
                      ? 'Conditions did not match'
                      : 'Execution error'}
                  </AlertTitle>
                  <AlertDescription>{execution.errorMessage}</AlertDescription>
                </Alert>
              )}

              {execution.status === WorkflowExecutionStatus.STOPPED && (
                <Alert>
                  <AlertTriangle />
                  <AlertTitle>Run stopped early</AlertTitle>
                  <AlertDescription>
                    {stoppedBy
                      ? `A stop condition matched (${stoppedBy}), so the workflow ended before its remaining steps ran.`
                      : 'A stop condition matched, so the workflow ended before its remaining steps ran.'}
                  </AlertDescription>
                </Alert>
              )}

              {execution.status === WorkflowExecutionStatus.CONDITION_UNMET && (
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-medium">Condition trace</h3>
                  <ConditionTraceTable trace={conditionTrace} />
                </section>
              )}

              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-medium">Action steps</h3>
                {steps.length === 0 ? (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                    No action steps were recorded.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border">
                    <Table className="min-w-[640px]">
                      <TableHeader className="bg-muted/40">
                        <TableRow>
                          <TableHead>Step</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Completed</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {steps.map((step) => {
                          const { edge, caseLabel, legacyNodeId, statusCode } =
                            parseStepResultData(step.resultData)
                          // Prefer the typed column; fall back to result_data
                          // for executions recorded before it existed.
                          const nodeId = step.nodeId || legacyNodeId || ''
                          const nodeLabel = stepDisplayLabel(
                            nodeId ? nodeLabels.get(nodeId) : undefined,
                            step.actionType
                          )
                          const actionLabel = humanizeActionType(step.actionType)
                          const annotations = [
                            // When the node resolved to a label, the action type
                            // becomes the secondary detail rather than the title.
                            nodeLabel ? actionLabel : undefined,
                            routingAnnotation(step.actionType, edge, caseLabel),
                            webhookAnnotation(step.actionType, statusCode),
                            step.attempt > 0 ? `attempt ${step.attempt + 1}` : undefined,
                            !nodeLabel && nodeId ? `node: ${nodeId}` : undefined,
                          ].filter((part): part is string => !!part)
                          return (
                            <TableRow key={step.id}>
                              <TableCell className="font-mono text-xs">
                                {step.actionIndex + 1}
                              </TableCell>
                              <TableCell>
                                <span>{nodeLabel ?? actionLabel}</span>
                                {annotations.length > 0 && (
                                  <span className="ml-1.5 text-xs text-muted-foreground">
                                    ({annotations.join(' · ')})
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={workflowExecutionStepStatusVariant(step.status)}>
                                  {workflowExecutionStepStatusLabel(step.status)}
                                </Badge>
                              </TableCell>
                              <TableCell className="tabular-nums">
                                {step.completedAt
                                  ? formatTimestamp(step.completedAt, 'MMM d, yyyy h:mm a')
                                  : '-'}
                              </TableCell>
                              <TableCell className="max-w-[220px] truncate text-muted-foreground">
                                {step.errorMessage || '-'}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </section>
            </>
          )}
        </SheetBody>

        <SheetFooter>
          {canRetry && (
            <div className="flex w-full flex-col gap-2 sm:items-end">
              <p className="text-xs text-muted-foreground">
                Retrying resumes from the failed step — steps that already completed won&apos;t run
                again.
              </p>
              <Button onClick={handleRetry} disabled={isPending}>
                {isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RotateCcw className="size-4" />
                )}
                Retry failed run
              </Button>
            </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <div className="mt-1 truncate text-sm">{children}</div>
    </div>
  )
}
